// Win Tool (launcher) campaign-member and sync-status wire contracts: the ONE
// schema, the ONE fetcher and the ONE react-query key each, for every app that
// reads `GET /api/tools/launcher/campaigns/:id/members` or
// `GET /api/tools/launcher/settings/sync-status` (launcher and playbooks both
// cache them under the keys below). Same ownership rule as
// `launcher-campaign-schema.ts`: two apps may not cache two shapes under one
// key, so schema + fetcher + key live together and every reader goes through
// them. `__tests__/launcher-campaigns-query-key.test.ts` pins the keys.
//
// Why the member row is LOOSE at runtime: the route spreads a bare
// `SELECT * FROM launcher_sfdc_campaign_members`, and launcher's grid reads
// unmodeled columns (lead_name, account_owner_id, ...) from the same cache
// entry this fetcher fills. A strip-mode parse would silently drop them for
// the grid. The EXPORTED type stays exact (no index signature) so a typo on
// `member.x` is still a compile error: `CampaignMemberShape` declares the
// modeled columns, `.loose()` is applied only when parsing.
//
// Every key below is a real column of `launcher_sfdc_campaign_members` (see
// prisma/schema/migrations). Required-nullable keys are columns every row
// carries (SELECT *); `.nullish()` keys are the ones the type has always
// declared optional. `open_pipeline` is `numeric` with no precision, which
// Prisma hands back as a Decimal, i.e. a JSON string on the wire; no reader
// does arithmetic on it, so the schema accepts both forms rather than lie.
import { z } from 'zod';

import { fetchJson } from './fetch-json';

type ApiFetch = (input: RequestInfo, init?: RequestInit & { timeoutMs?: number }) => Promise<Response>;

/** Widen the narrow `ApiFetch` the app wrappers use to the `URL`-accepting shape `fetchJson` takes. */
function widen(apiFetch: ApiFetch) {
  return (input: RequestInfo | URL, init?: RequestInit & { timeoutMs?: number }) =>
    apiFetch(input instanceof URL ? input.href : input, init);
}

const CampaignMemberShape = z.object({
  id: z.string(),
  campaign_id: z.string(),
  contact_id: z.string().nullable(),
  lead_id: z.string().nullish(),
  lead_email: z.string().nullish(),
  contact_name: z.string().nullable(),
  contact_first_name: z.string().nullish(),
  contact_last_name: z.string().nullish(),
  contact_email: z.string().nullable(),
  contact_title: z.string().nullable(),
  mailing_city: z.string().nullish(),
  mailing_state: z.string().nullish(),
  account_name: z.string().nullable(),
  sales_tier: z.string().nullable(),
  gtme_owner_name: z.string().nullable(),
  gtme_owner_email: z.string().nullable(),
  gtme_owner_first_name: z.string().nullish(),
  gtme_owner_role: z.string().nullish(),
  gtme_meeting_link: z.string().nullish(),
  gtme_first_name: z.string().nullish(),
  last_gtme_meeting_date: z.string().nullable(),
  gs_owner_name: z.string().nullable(),
  gs_owner_email: z.string().nullable(),
  gs_owner_first_name: z.string().nullish(),
  gs_meeting_link: z.string().nullish(),
  last_gs_meeting_date: z.string().nullish(),
  claydr_name: z.string().nullable(),
  claydr_email: z.string().nullable(),
  claydr_first_name: z.string().nullish(),
  claydr_meeting_link: z.string().nullish(),
  last_claydr_meeting_date: z.string().nullish(),
  contact_account_id: z.string().nullish(),
  account_last_gtme_meeting_date: z.string().nullish(),
  account_last_gs_meeting_date: z.string().nullish(),
  account_last_claydr_meeting_date: z.string().nullish(),
  last_follow_up_date: z.string().nullish(),
  primary_met_with_name: z.string().nullish(),
  primary_met_with_first_name: z.string().nullish(),
  primary_met_with_email: z.string().nullish(),
  owner_name: z.string().nullish(),
  owner_email: z.string().nullish(),
  owner_role: z.string().nullish(),
  cm_status: z.string().nullable(),
  created_date: z.string().nullish(),
  has_responded: z.boolean().nullish(),
  notes__c: z.string().nullish(),
  suppressed: z.boolean().nullable(),
  suppression_reasons: z.string().nullable(),
  no_longer_at_company: z.boolean().nullable(),
  fit_score: z.number().nullable(),
  contact_stage: z.string().nullish(),
  persona: z.string().nullish(),
  job_level: z.string().nullish(),
  account_fit: z.string().nullish(),
  named_account_status: z.string().nullish(),
  account_suppressed: z.boolean().nullish(),
  account_suppression_reasons: z.string().nullish(),
  competitor: z.boolean().nullish(),
  agency: z.boolean().nullish(),
  potential_contract_value: z.number().nullish(),
  arr_active_subscriptions: z.number().nullish(),
  has_opted_out_of_email: z.boolean().nullish(),
  do_not_call: z.boolean().nullish(),
  account_type: z.string().nullish(),
  product_tier: z.string().nullish(),
  plan_interval: z.string().nullish(),
  number_of_open_opps: z.number().nullish(),
  qualification_status: z.string().nullish(),
  last_opportunity_stage: z.string().nullish(),
  last_gtme_email_date: z.string().nullish(),
  last_gs_email_date: z.string().nullish(),
  partner_type: z.string().nullish(),
  open_pipeline: z.union([z.number(), z.string()]).nullish(),
  company_segment: z.string().nullish(),
  persona_tier: z.string().nullish(),
  is_requalified: z.boolean().nullish(),
});

export type CampaignMember = z.infer<typeof CampaignMemberShape>;

const CampaignMembersResponseSchema = z.array(CampaignMemberShape.loose());

/** `['launcher','campaigns', id, 'members']`; use as the `queryKey` of every read of this fetcher. */
export const launcherCampaignMembersQueryKey = (id: string) =>
  ['launcher', 'campaigns', id, 'members'] as const;

/** One campaign's members, parsed. Rejects with a `malformed` `FetchJsonError` on a wrong shape. */
export async function fetchLauncherCampaignMembers(apiFetch: ApiFetch, id: string): Promise<CampaignMember[]> {
  return fetchJson(widen(apiFetch), CampaignMembersResponseSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(id)}/members`);
}

/**
 * Wire shape of `GET /api/tools/launcher/settings/sync-status`
 * (`mini-apps/launcher/api.ts#createGetSyncStatus`).
 */
const LauncherSyncStatusSchema = z.object({
  campaign_hourly: z
    .object({
      status: z.string(),
      campaigns_synced: z.number().nullable(),
      completed_at: z.string().nullable(),
    })
    .nullable(),
  last_error: z.string().nullable(),
});
export type LauncherSyncStatus = z.infer<typeof LauncherSyncStatusSchema>;

/** `['launcher','sync-status']`: cached by both launcher and playbooks. */
export const LAUNCHER_SYNC_STATUS_QUERY_KEY = ['launcher', 'sync-status'] as const;

export async function fetchLauncherSyncStatus(apiFetch: ApiFetch): Promise<LauncherSyncStatus> {
  return fetchJson(widen(apiFetch), LauncherSyncStatusSchema, '/api/tools/launcher/settings/sync-status');
}

/**
 * Wire shape of `POST /api/tools/launcher/campaigns/:id/sync-members`
 * (`mini-apps/launcher/api.ts#createSyncMembers`).
 */
const LauncherMemberSyncResultSchema = z.object({
  synced: z.boolean(),
  member_count: z.number(),
  synced_at: z.string(),
});
export type LauncherMemberSyncResult = z.infer<typeof LauncherMemberSyncResultSchema>;

/**
 * Live, awaited resync of one campaign's members. A POST: when it rejects with
 * a `malformed` or `aborted` `FetchJsonError` (see `writeMayHaveLanded`) the
 * mirror may already be fresh, so callers refetch the members rather than
 * report a failure.
 */
export async function syncLauncherCampaignMembers(
  apiFetch: ApiFetch,
  id: string,
  timeoutMs: number,
): Promise<LauncherMemberSyncResult> {
  return fetchJson(widen(apiFetch), LauncherMemberSyncResultSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(id)}/sync-members`, {
    method: 'POST',
    timeoutMs,
  });
}
